<script lang="ts">
	import * as Glide from "@glidejs/glide";
	import type { ActionReturn } from "svelte/action";
	import { disableGlidePerSlideAmount } from "../utils/disableGlidePerSlideAmount";
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import Icon from "./Icon.svelte";
	import glideArrow from "../../../core/assets/icons/glide-arrow.svg?raw";
	import glideBack from "../../../core/assets/icons/glide-back.svg?raw";
	import { textLengthLimit } from "../../../core/utils/textLengthLimit";
	import { getTenant } from "../contexts/tenantContextKey";
	import Content from "./Content.svelte";
	import t1 from "../assets/images/technology/1.png?webp";
	import t2 from "../assets/images/technology/2.jpeg?webp";
	import t3 from "../assets/images/technology/3.png?webp";
	import translations from "../templates/RnD.translations.json";
	import { getTranslate } from "../utils/getTranslate";
	import { flyInOnScroll } from "../utils/flyInOnScroll.js";

	const translate = getTranslate(translations);

	let customClass = "";
	export { customClass as class };

	const slides = [
		{
			img: t1,
			title: {
				cs: "RTG s CT modulem stanoviště",
				en: "RTG with CT module",
			},
			text: {
				cs: "<b>RTG stanoviště s CT modulem</b> umožňuje v rámci prototypů ale i sériové výroby najít i tu nejmenší vadu a zachytit tak případnou chybu ve vývoji ještě před spuštěním sériové výroby.",
				en: "<b>The RTG with a CT module</b> allows for the detection of even the smallest defect in prototypes and series production, catching potential errors in development before mass production begins.",
			},
		},
		{
			img: t2,
			title: {
				cs: "3D optický skener Keyence",
				en: "RTG with CT module",
			},
			text: {
				cs: "<b>3D optický skener Keyence</b> umožňuje skenování metodou Place-and-Click, což nám dovoluje pořizovat spolehlivá data. 16 milionů datových bodů zachycených na jeden sken umožňuje přesné měření složitých tvarů a drobných povrchových prvků. Díky tomuto skeneru dokážeme porovnávat díly přímo s jejich CAD modelem a okamžitě zjistit případné odchylky.",
				en: "<b>The 3D optical scanner Keyence</b> enables scanning using the Place-and-Click method, allowing us to capture reliable data. With 16 million data points captured in a single scan, it provides precise measurement of complex shapes and small surface details. Thanks to this scanner, we can compare parts directly with their CAD models and instantly detect any deviations.",
			},
		},
		{
			img: t3,
			title: {
				cs: "Naše vlastní moderně vybavená zkušebna",
				en: "RTG with CT module",
			},
			text: {
				cs: "<b>Naše vlastní moderně vybavená zkušebna</b> disponuje vysokorychlostní kamerou, termo kamerou, akustickou komorou a komorou pro zkoušky hořlavosti . Toto všechno doplňují naše teplotní klimatické a korozní komory, abychom zaručili, že zákazník vždy dostane robustní produkty, na které se může spolehnout.",
				en: "<b>Our own modern testing facility</b> is equipped with a high-speed camera, thermal camera, acoustic chamber, and a flammability testing chamber. Complementing these are our temperature climate and corrosion chambers, ensuring that the customer always receives robust products they can rely on.",
			},
		},
	];

	const buttons: string[] = [glideBack, glideArrow];
	let currentIndex = 0;

	const { locale } = getTenant();

	function glide(element: HTMLDivElement): ActionReturn {
		const glideOptions = {
			type: "slider",
			startAt: 0,
			focusAt: 0,
			perView: 1.25,
			hoverpause: false,
			animationDuration: 750,
			animationTimingFunc: "ease-in-out",
			keyboard: true,
			cloningRatio: 2,
			rewind: false,
			dragThreshold: false,
			breakpoints: {
				2500: { perView: 1.25 },
				1880: { perView: 1.2 },
				1820: { perView: 1.15 },
				1700: { perView: 1.1 },
				1635: { perView: 1.05 },
				1324: { perView: 1 },
			},
		} satisfies Partial<Glide.Options>;

		// eslint-disable-next-line new-cap
		const glide = new Glide.default(element, { ...glideOptions }).mount();
		glide.on("resize", () => {
			disableGlidePerSlideAmount(glide, element);
		});

		glide.on(["mount.before", "run"], () => {
			currentIndex = glide.index;
		});

		return {
			destroy(): void {
				glide.destroy();
			},
		};
	}
</script>

<div class={customClass} use:flyInOnScroll>
	<div class="min-[2000px]:container">
		<div use:glide class="glide relative w-full pb-12 min-[500px]:pb-0">
			<div class="container mb-[5.5rem]">
				<div class="flex h-[3.5625rem] w-full items-end justify-between sm:gap-5 lg:mt-[12rem] lg:gap-[0rem]">
					<span class="-mb-12 text-[2.75rem] font-semibold leading-[1.13] lg:mb-0 lg:text-[4.625rem]">
						{translate("technological")} <br />
						{translate("background")}
					</span>
					<div
						class="hidden h-[3.5625rem] w-fit justify-center gap-[3rem] lg:flex lg:justify-normal"
						data-glide-el="controls"
					>
						{#each buttons as button, index}
							<button
								aria-label={`Posuňte ${index === 0 ? "doleva" : "doprava"}`}
								data-glide-dir={index === 0 ? "<" : ">"}
								class="focus:outline-none"
							>
								<Icon
									icon={button}
									class="h-[3.5625rem] text-black lg:h-auto {(index - 1 && currentIndex === 0) ||
									(currentIndex === slides.length - 1 && index === 1)
										? 'opacity-20'
										: ''}"
								/>
							</button>
						{/each}
					</div>
					<div class="hidden h-[3.5625rem] w-[39%] items-center lg:flex">
						<div class="h-[0.0625rem] w-full bg-black"></div>
					</div>
				</div>
			</div>
			<div class="glide__track lg:bg-tertiary relative z-20 overflow-hidden lg:h-[44.0625rem]" data-glide-el="track">
				<div class="glide__slides lg:bg-tertiary inset-0 flex lg:h-[44.0625rem]">
					{#each slides as slide, i}
						<div class="glide__slide flex justify-center bg-transparent lg:h-[44.0625rem]">
							<div
								class="relative z-10 hidden h-full w-full flex-col items-center justify-between lg:flex {currentIndex ===
								i
									? 'lg:ml-[4.375rem]'
									: ''}"
							>
								<div
									class="absolute top-[20rem] flex h-fit flex-col justify-between self-end bg-white transition-all duration-300 ease-in-out lg:left-4 lg:w-[42rem] lg:px-[3.625rem] lg:pb-16 lg:pt-[4.625rem] xl:w-[49.125rem] {i ===
									0
										? 'xl:left-0'
										: 'bottom-[4.5rem] xl:left-[6.69rem]'} {currentIndex === i ? 'opacity-100' : 'opacity-0'}"
								>
									<div class="lg:text-2md text-wrap text-[1.125rem]">
										<Content content={textLengthLimit(slide.text[locale])} />
									</div>
								</div>

								<StaticPicture
									image={slide.img}
									alt={slide.title[locale]}
									width={1452}
									height={705}
									imgClass="w-full h-full object-cover"
									class="h-full w-full max-w-[90.75rem] items-center overflow-hidden transition-all duration-200 ease-in-out xl:w-[90.75rem] {currentIndex ===
									i
										? 'lg:pl-[3rem]'
										: ''}"
								/>
							</div>
							<div class="relative lg:hidden">
								<div
									class="absolute bottom-[0.97rem] left-[1.19rem] right-[1.19rem] z-10 bg-white/95 px-[2rem] py-[3rem] lg:hidden"
								>
									<div class="lg:text-2md text-wrap text-[1.125rem]">
										<Content content={textLengthLimit(slide.text[locale])} />
									</div>
								</div>
								<StaticPicture
									image={slide.img}
									alt={slide.title[locale]}
									width={1280}
									height={1024}
									imgClass="h-full w-full object-cover"
									class="h-[38rem] w-full lg:hidden"
								/>
							</div>
						</div>
					{/each}
				</div>
				<div
					class="container inset-0 z-30 mb-[7rem] flex justify-center gap-[7rem] pt-[4.19rem] lg:absolute lg:mb-0 lg:hidden lg:flex-col lg:justify-normal lg:pt-[9rem]"
				>
					<div class="-mt-4 flex w-full justify-center gap-16 lg:justify-normal" data-glide-el="controls">
						{#each buttons as button, index}
							<button
								aria-label={`Posuňte ${index === 0 ? "doleva" : "doprava"}`}
								data-glide-dir={index === 0 ? "<" : ">"}
								class="focus:outline-none"
							>
								<Icon
									icon={button}
									class="h-[3.5625rem] text-black lg:h-auto {(index - 1 && currentIndex === 0) ||
									(currentIndex === slides.length - 1 && index === 1)
										? 'opacity-20'
										: ''}"
								/>
							</button>
						{/each}
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
